import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import { BackToTop } from "@/components/feature/BackToTop";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles, categories } from "@/mocks/articles";
import { usePageMeta, SITE_ORIGIN } from "@/hooks/usePageMeta";

const LIST_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: "深度复盘｜看料吃瓜",
  url: `${SITE_ORIGIN}/insights`,
  inLanguage: "zh-CN",
  description: "看料吃瓜全部深度复盘合集，按文娱观察、网络热点、产业逻辑、舆情复盘等主题分类浏览。",
};

export default function Insights() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") ?? "";
  const query = searchParams.get("q") ?? "";
  const [input, setInput] = useState(query);

  useEffect(() => {
    setInput(query);
  }, [query]);

  const activeCategoryName = categories.find((item) => item.slug === activeCategory)?.name;

  const filtered = useMemo(() => {
    const kw = query.trim().toLowerCase();
    return articles.filter((item) => {
      const matchCategory = activeCategory ? item.categorySlug === activeCategory : true;
      const matchQuery = kw
        ? item.title.toLowerCase().includes(kw) ||
          item.dek.toLowerCase().includes(kw) ||
          item.tags.some((tag) => tag.toLowerCase().includes(kw))
        : true;
      return matchCategory && matchQuery;
    });
  }, [activeCategory, query]);

  const pageJsonLd = useMemo(
    () => ({
      ...LIST_JSON_LD,
      mainEntity: {
        "@type": "ItemList",
        itemListElement: filtered.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `${SITE_ORIGIN}/insights/${item.slug}`,
          name: item.title,
        })),
      },
    }),
    [filtered]
  );

  usePageMeta({
    title: "深度复盘合集｜看料吃瓜深挖全网热点事件",
    description:
      "浏览看料吃瓜的全部深度复盘：文娱观察、网络热点、产业逻辑、舆情复盘、人物侧写与平台生态。按主题筛选，找到你想深挖的事件背后完整逻辑。",
    path: "/insights",
    keywords: "看料吃瓜复盘,深度复盘合集,吃瓜深挖分析,热点事件梳理",
    type: "website",
    jsonLd: pageJsonLd,
  });

  const setCategory = (slug: string) => {
    const next = new URLSearchParams(searchParams);
    if (slug) next.set("category", slug);
    else next.delete("category");
    setSearchParams(next);
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (input.trim()) next.set("q", input.trim());
    else next.delete("q");
    setSearchParams(next);
  };

  const resetAll = () => {
    setSearchParams(new URLSearchParams());
    setInput("");
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main>
        <nav aria-label="面包屑" className="border-b border-background-200 bg-background-100">
          <div className="mx-auto flex max-w-editorial items-center gap-2 px-4 py-3 text-xs text-foreground-500 md:px-6">
            <Link to="/" className="transition-colors hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <span className="text-foreground-800">深度复盘</span>
          </div>
        </nav>

        <header className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-editorial px-4 pb-10 pt-8 md:px-6 md:pb-12 md:pt-10">
            <p className="mb-3 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-primary-600">
              <span className="inline-block h-3 w-0.5 bg-primary-500" aria-hidden="true"></span>
              全部内容
            </p>
            <h1 className="font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-[40px]">
              {activeCategoryName ? `${activeCategoryName} · 深度复盘` : "深度复盘合集"}
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-loose text-foreground-600 md:text-base">
              这里收录了看料吃瓜的全部深度复盘。每篇都从事件时间线讲起，拆开背景脉络与产业逻辑，帮你看清热点背后的完整结构，而不是停留在情绪层面。
            </p>
          </div>
        </header>

        <div className="sticky top-[57px] z-30 border-b border-background-200 bg-background-50/95 backdrop-blur md:top-[97px]">
          <div className="mx-auto flex max-w-editorial flex-col gap-3 px-4 py-4 md:flex-row md:items-center md:justify-between md:px-6">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setCategory("")}
                className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs transition-colors ${
                  activeCategory === ""
                    ? "border-primary-500 bg-primary-500 text-background-50"
                    : "border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                全部
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  type="button"
                  onClick={() => setCategory(cat.slug)}
                  className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs transition-colors ${
                    activeCategory === cat.slug
                      ? "border-primary-500 bg-primary-500 text-background-50"
                      : "border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <form onSubmit={submitSearch} className="flex items-center gap-2 md:w-[280px]">
              <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2 focus-within:border-primary-400">
                <i className="ri-search-line text-sm text-foreground-500" aria-hidden="true"></i>
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="搜索标题或关键词"
                  aria-label="搜索复盘"
                  className="w-full bg-transparent text-sm outline-none placeholder:text-foreground-400"
                />
              </div>
              <button
                type="submit"
                className="whitespace-nowrap rounded-md bg-foreground-950 px-3.5 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索
              </button>
            </form>
          </div>
        </div>

        <section className="mx-auto max-w-editorial px-4 py-10 md:px-6 md:py-14">
          <div className="mb-6 flex items-center justify-between">
            <p className="text-sm text-foreground-500">
              共找到 <span className="font-medium text-foreground-950">{filtered.length}</span> 篇复盘
              {query && (
                <>
                  ，关键词「<span className="text-primary-600">{query}</span>」
                </>
              )}
            </p>
            {(activeCategory || query) && (
              <button
                type="button"
                onClick={resetAll}
                className="inline-flex items-center gap-1 text-xs text-foreground-500 transition-colors hover:text-primary-600"
              >
                <i className="ri-refresh-line" aria-hidden="true"></i>
                清除筛选
              </button>
            )}
          </div>

          {filtered.length > 0 ? (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((item) => (
                <ArticleCard key={item.slug} article={item} variant="standard" />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-20 text-center">
              <i className="ri-inbox-archive-line text-3xl text-foreground-400" aria-hidden="true"></i>
              <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-900">暂时没有匹配的复盘</h2>
              <p className="mt-2 max-w-sm text-sm leading-relaxed text-foreground-500">
                换个关键词或选择其他栏目试试，也可以清除全部筛选查看完整内容。
              </p>
              <button
                type="button"
                onClick={resetAll}
                className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                查看全部复盘
              </button>
            </div>
          )}
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}